/**
 * Shared type definitions for social media components
 */

import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

// ============================================================================
// Label Query Types (for unified filtering)
// ============================================================================

/**
 * Numeric range filter for filtering labels by value range.
 * Useful for numeric metrics like views, engagement rates, etc.
 */
export interface RangeFilter {
  min?: number
  max?: number
}

/**
 * Unified filter for pipelines supporting both text and numeric filters.
 * Text filters use exact match (OR within values, AND across keys).
 * Range filters use numeric comparison after parsing the label value.
 */
/** Date range filter for filtering records by source_date */
export interface DateRange {
  from?: string  // ISO 8601 date string, e.g. "2026-01-01"
  to?: string    // ISO 8601 date string, e.g. "2026-03-31"
}

/**
 * Filter over entities (videos / posts / accounts) - the shared entity filter
 * shape, mirroring the Go `types.EntityQuery`.
 */
export interface EntityQuery {
  /** Entity label filters - exact match (AND across keys, OR within values) */
  labelFilters?: Record<string, string[]>
  /** Platform filter (OR within values), e.g. ["tiktok", "instagram"] */
  platforms?: string[]
  /**
   * Sprinklr campaign ids (OR within values), e.g. ["2531_13"].
   *
   * NOT `labelFilters.campaign`: that carries whatever display name the channel
   * manifest stamped, this is the real column holding Sprinklr's own id. The
   * `none` / `unknown` sentinels are valid selections.
   */
  campaigns?: string[]
  /** Entity kind filter (OR within values), e.g. ["video"] */
  kinds?: string[]
  /**
   * Business source of the post (OR within values): "social", "influencer",
   * later "pr" and "internal". Not the fetch driver (Sprinklr, Apify).
   */
  sources?: string[]
  /** Published date range - filters entities by their published_date column */
  publishedDateRange?: DateRange
  /** Numeric filters over entity metrics, keyed by metric key */
  metricRanges?: Record<string, RangeFilter>
  /** Direct entity selection (drill-down / "this video only") */
  entityIds?: string[]
  /** Free text matched against entity name/url */
  search?: string
}

export interface LabelQuery {
  /** Text filters - exact match (OR within values for each key) */
  textFilters?: Record<string, string[]>
  /** Range filters - numeric min/max bounds */
  rangeFilters?: Record<string, RangeFilter>
  /** Published date range - filters pipelines by video publish date */
  publishedDateRange?: DateRange
  /** Comment date range - filters records by comment creation date */
  commentDateRange?: DateRange
  /** Record-metadata equality filters (single value per key), e.g. {source:"apify-tiktok"} */
  metadataFilters?: Record<string, string>
  /**
   * Record-metadata EXCLUSIONS, e.g. {author_type:["brand"]}: a record is kept
   * unless its metadata matches one of these pairs. A record that lacks the key
   * entirely is kept — which is exactly what `metadataFilters` cannot express.
   */
  metadataExclusions?: Record<string, string[]>
  /**
   * Positive multi-select: the record's metadata must match ANY of a key's
   * listed values (OR within a key, AND across keys). The form
   * `metadataFilters` cannot express — see goapi types.LabelQuery.MetadataAny,
   * which also records why it is never lowered as a negative.
   */
  metadataAny?: Record<string, string[]>
  /** Entity filter - restricts records to the entities (videos/posts) matching it */
  entity?: EntityQuery
}

/**
 * Check if a label value looks numeric (for determining filter type).
 * Handles: plain numbers, percentages, numbers with commas.
 */
export function isNumericLabelValue(value: string): boolean {
  if (!value) return false
  // Remove % and commas, then check if it's a valid number
  const cleaned = value.replace(/%/g, '').replace(/,/g, '').trim()
  return !isNaN(parseFloat(cleaned)) && isFinite(parseFloat(cleaned))
}

/**
 * Parse a label value as a number.
 * Handles: plain numbers, percentages (61%), numbers with commas (1,234).
 */
export function parseNumericLabelValue(value: string): number | null {
  if (!value) return null
  const cleaned = value.replace(/%/g, '').replace(/,/g, '').trim()
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

/**
 * Data type for cell values
 */
export type DataType = 'freq' | 'colPct' | 'rowPct';

/**
 * Common props shared by chart wrapper components
 */
export interface BaseChartProps {
  /** The PlatinumData to display */
  data: PlatinumData;
  /** Chart title */
  title?: string;
  /** Which data type to display */
  dataType?: DataType;
  /** Which column to chart (default: first non-Base column) */
  column?: string;
  /** Chart height (ignored when fillContainer is true) */
  height?: number | string;
  /** When true, Paper fills its parent container height */
  fillContainer?: boolean;
}

/**
 * Props for bar-style charts (CategoryBarChart, EmotionBarChart)
 *
 * The only surviving consumer of BaseChartProps. The prop types for
 * SentimentPieChart, ComparisonChart, MetricCard, MetricCardGroup and
 * QualityBreakdown went with those components — they described components
 * nothing rendered.
 */
export interface BarChartProps extends BaseChartProps {
  /** Chart orientation */
  orientation?: 'horizontal' | 'vertical';
}
